// Knowledge Base, index + detail in one component (route param drives view).
//   /kb           → index (categories + recent articles)
//   /kb/:slug     → article detail
import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { Loader2, AlertCircle, ArrowLeft, BookOpen, ArrowUpRight, Clock, Rss, Copy, Check, Search, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import SiteLayout from "@/components/site/SiteLayout";
import Icon from "@/components/site/Icon";
import { fetchKbCategories, fetchKbList, fetchKbArticle, flatten } from "@/lib/cmsExtras";
import { getCmsApiBaseUrl } from "@/lib/cmsBase";
import { useInternalLinks } from "@/lib/internalLinks";
import RelatedArticles from "@/components/site/RelatedArticles";

const KB_RSS_URL = `${getCmsApiBaseUrl()}/kb-articles/rss.xml`;

function KbRssWidget() {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(KB_RSS_URL);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      // fallback: open in new tab so user can copy from address bar
      window.open(KB_RSS_URL, "_blank", "noopener,noreferrer");
    }
  };
  return (
    <div
      data-testid="kb-rss-widget"
      className="rounded-2xl border border-border bg-secondary/40 p-5 flex flex-col sm:flex-row sm:items-center gap-4 justify-between"
    >
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-full bg-accent/15 text-accent inline-flex items-center justify-center shrink-0">
          <Rss className="h-5 w-5" />
        </div>
        <div>
          <div className="font-display text-base font-bold text-primary">Subscribe to the knowledge base</div>
          <p className="text-sm text-muted-foreground">
            New articles delivered to your Feedly, Inoreader or RSS reader of choice.
          </p>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <a
          href={KB_RSS_URL}
          target="_blank"
          rel="noreferrer"
          data-testid="kb-rss-open"
          className="inline-flex items-center gap-1.5 text-xs font-bold text-primary hover:text-accent transition-colors"
        >
          Open feed <ArrowUpRight className="h-3.5 w-3.5" />
        </a>
        <button
          type="button"
          onClick={copy}
          data-testid="kb-rss-copy"
          className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-4 py-2 text-xs font-bold hover:bg-primary/90 transition-colors"
        >
          {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
          {copied ? "Copied" : "Copy RSS URL"}
        </button>
      </div>
    </div>
  );
}

function Empty({ message = "No articles yet.", hint }) {
  return (
    <div className="rounded-2xl border border-dashed border-border bg-secondary/30 p-10 text-center">
      <BookOpen className="h-8 w-8 mx-auto text-muted-foreground" />
      <div className="mt-3 font-display text-lg text-primary font-bold">{message}</div>
      {hint && <p className="mt-2 text-sm text-muted-foreground max-w-md mx-auto">{hint}</p>}
    </div>
  );
}

function Bullets({ items, skipHref, linkify }) {
  if (!items || !items.length) return null;
  return (
    <ul className="mt-3 space-y-2">
      {items.map((p, i) => (
        <li key={i} className="text-sm md:text-base text-primary/85 leading-relaxed">
          {linkify ? linkify(p, { skipHref, key: `il-b${i}` }) : p}
        </li>
      ))}
    </ul>
  );
}

export default function KBPage() {
  const { slug } = useParams();
  return slug ? <ArticleView slug={slug} /> : <IndexView />;
}

function IndexView() {
  const [q, setQ] = useState("");
  const [cats, setCats] = useState([]);
  const [items, setItems] = useState([]);
  const [product, setProduct] = useState("all");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const location = useLocation();

  useEffect(() => {
    (async () => {
      try {
        const [c, list] = await Promise.all([fetchKbCategories(), fetchKbList()]);
        setCats((c.data || []).map(flatten));
        setItems((list.data || []).map(flatten));
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const query = q.trim().toLowerCase();
  const activeCategory = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get("cat") || "all";
  }, [location.search]);

  // Product taxonomy comes from articles' relatedProducts field. Auto-built so
  // a future article tagged with "ai" just appears as a new chip.
  const productOptions = (() => {
    const counts = new Map();
    for (const a of items) {
      for (const p of a.relatedProducts || []) counts.set(p, (counts.get(p) || 0) + 1);
    }
    const labels = { "cosec-app": "CoSec App", coverify: "CoVerify", cosearch: "CoSearch", coform: "CoForm", ai: "AI" };
    return Array.from(counts.entries()).map(([key, count]) => ({ key, label: labels[key] || key, count }));
  })();

  const filtered = useMemo(() => {
    return items.filter((a) => {
      const searchable = [
        a.title,
        a.summary,
        a.slug,
        a.category?.name,
        a.category?.slug,
        ...(Array.isArray(a.tags) ? a.tags : []),
        ...(Array.isArray(a.relatedProducts) ? a.relatedProducts : []),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      const matchesQ = !query || searchable.includes(query);
      const matchesProduct = product === "all" || (a.relatedProducts || []).includes(product);
      const categorySlug = a.category?.slug || "";
      const categoryName = a.category?.name || "";
      const matchesCategory =
        activeCategory === "all"
        || categorySlug.toLowerCase() === activeCategory.toLowerCase()
        || categoryName.toLowerCase() === activeCategory.toLowerCase();
      return matchesQ && matchesProduct && matchesCategory;
    });
  }, [items, query, product, activeCategory]);

  return (
    <SiteLayout
      title="Knowledge Base"
      description="Help articles, how-tos, and product guides for CoSec Global users."
      type="website"
      breadcrumbs={[
        { name: "Home", href: "https://www.cosecglobal.com/" },
        { name: "Knowledge Base", href: "https://www.cosecglobal.com/kb" },
      ]}
    >
      <section className="dark-hero py-16 md:py-20 border-b border-border">
        <div className="container-cv">
          <span className="section-eyebrow text-accent before:bg-accent">Knowledge base</span>
          <h1 className="mt-3 font-display text-3xl md:text-5xl font-bold tracking-tight leading-[1.05]">
            Get the most out of CoSec Global.
          </h1>
          <p className="mt-4 text-base md:text-lg text-white/70 max-w-2xl">
            Articles, how-tos and product walkthroughs from our team, designed to answer the questions UK Cosec, accounting and law teams ask most.
          </p>
          <div className="mt-7 max-w-xl">
            <div className="relative rounded-2xl border border-white/15 bg-white/96 shadow-[0_24px_60px_-24px_rgba(10,20,35,0.45)]">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
              <Input
                data-testid="kb-search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search the knowledge base..."
                className="h-12 pl-10 pr-10 border-0 bg-transparent text-primary placeholder:text-muted-foreground shadow-none focus-visible:ring-0"
              />
              {q && (
                <button
                  type="button"
                  onClick={() => setQ("")}
                  aria-label="Clear search"
                  className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full p-1 text-muted-foreground hover:bg-secondary hover:text-primary transition-colors"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
            <div className="mt-2 text-xs text-white/65">
              Search title, summary, tags, slug, category, or product.
            </div>
          </div>
          {productOptions.length > 0 && (
            <div className="mt-5 flex flex-wrap gap-2" data-testid="kb-product-filters">
              <button
                type="button"
                data-testid="kb-product-all"
                onClick={() => setProduct("all")}
                className={`px-3.5 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider transition-colors ${
                  product === "all"
                    ? "bg-accent text-primary"
                    : "bg-white/10 text-white/80 hover:bg-white/20"
                }`}
              >
                All ({items.length})
              </button>
              {productOptions.map((p) => (
                <button
                  type="button"
                  key={p.key}
                  data-testid={`kb-product-${p.key}`}
                  onClick={() => setProduct(p.key)}
                  className={`px-3.5 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider transition-colors ${
                    product === p.key
                      ? "bg-accent text-primary"
                      : "bg-white/10 text-white/80 hover:bg-white/20"
                  }`}
                >
                  {p.label} ({p.count})
                </button>
              ))}
            </div>
          )}
          {activeCategory !== "all" && (
            <div className="mt-4 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-3 py-1.5 text-xs font-medium text-white/85">
              Category: <span className="font-bold">{activeCategory}</span>
              <Link to="/kb" className="text-accent hover:text-white transition-colors">Clear</Link>
            </div>
          )}
        </div>
      </section>

      <section className="py-12 md:py-16">
        <div className="container-cv">
          {loading && (
            <div className="flex items-center gap-2 text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading articles…
            </div>
          )}
          {error && (
            <div className="rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700 inline-flex items-start gap-2">
              <AlertCircle className="h-4 w-4 mt-0.5" /> {error}
            </div>
          )}

          {!loading && !error && cats.length > 0 && (
            <>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
                {cats.map((c) => (
                  <Link
                    key={c.slug}
                    to={`/kb?cat=${encodeURIComponent(c.slug)}`}
                    data-testid={`kb-cat-${c.slug}`}
                    className="rounded-2xl border border-border bg-white p-5 hover:-translate-y-0.5 hover:border-accent/60 transition-all"
                  >
                    <div className="h-11 w-11 grid place-items-center rounded-xl bg-primary/10 text-primary">
                      <Icon name={c.iconKey} className="h-5 w-5" />
                    </div>
                    <div className="mt-4 font-display font-bold text-primary tracking-tight">{c.name}</div>
                    {c.description && <p className="mt-1 text-sm text-muted-foreground leading-relaxed">{c.description}</p>}
                  </Link>
                ))}
              </div>
              <div className="mb-12">
                <KbRssWidget />
              </div>
            </>
          )}

      {!loading && !error && filtered.length === 0 && (
        <Empty
              message={query || product !== "all" || activeCategory !== "all" ? "No articles match your filters." : "No articles published yet."}
              hint={query || product !== "all" || activeCategory !== "all"
                ? "Try clearing the search, category, or product filter."
                : "When CMS_PROVIDER=strapi and your team publishes KB articles in the Strapi admin, they'll appear here automatically."}
        />
      )}

          {!loading && !error && filtered.length > 0 && (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="kb-articles">
              {filtered.map((a) => (
                <Link
                  key={a.slug || a.id}
                  to={`/kb/${a.slug}`}
                  data-testid={`kb-article-${a.slug}`}
                  className="group rounded-2xl border border-border bg-white p-6 hover:-translate-y-1 hover:border-accent/60 hover:shadow-md transition-all"
                >
                  <h3 className="font-display text-lg font-bold text-primary tracking-tight group-hover:text-accent transition-colors">{a.title}</h3>
                  {a.summary && <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{a.summary}</p>}
                  <div className="mt-4 flex items-center gap-3 text-xs text-muted-foreground">
                    {a.readMins && <span className="inline-flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> {a.readMins} min</span>}
                    <ArrowUpRight className="h-3.5 w-3.5 ml-auto text-primary group-hover:text-accent" />
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>
    </SiteLayout>
  );
}

function ArticleView({ slug }) {
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [related, setRelated] = useState([]);
  const linkify = useInternalLinks();

  useEffect(() => {
    setLoading(true);
    fetchKbArticle(slug)
      .then((res) => setDoc(flatten(res.data)))
      .catch((e) => setError(e.status === 404 ? "not_found" : e.message))
      .finally(() => setLoading(false));
    fetchKbList()
      .then((res) => setRelated((res.data || []).map(flatten)))
      .catch(() => {});
  }, [slug]);

  if (loading) {
    return (
      <SiteLayout>
        <div className="container-cv py-20 text-muted-foreground inline-flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading article…</div>
      </SiteLayout>
    );
  }

  if (error === "not_found" || !doc) {
    return (
      <SiteLayout title="Article not found">
        <div className="container-cv py-24 text-center">
          <AlertCircle className="h-8 w-8 mx-auto text-muted-foreground" />
          <h1 className="mt-4 font-display text-2xl font-bold text-primary">Article not found</h1>
          <Link to="/kb" className="mt-4 inline-flex items-center gap-1 text-sm font-bold text-primary hover:text-accent">
            <ArrowLeft className="h-4 w-4" /> Back to Knowledge Base
          </Link>
        </div>
      </SiteLayout>
    );
  }

  return (
    <SiteLayout
      title={doc.title}
      description={doc.summary || ""}
      type="article"
      image={doc.cover}
      faq={Array.isArray(doc.faqs) ? doc.faqs : undefined}
      breadcrumbs={[
        { name: "Home", href: "https://www.cosecglobal.com/" },
        { name: "Knowledge Base", href: "https://www.cosecglobal.com/kb" },
        { name: doc.title, href: `https://www.cosecglobal.com/kb/${doc.slug || slug}` },
      ]}
      jsonLd={{
        "@context": "https://schema.org",
        "@type": "Article",
        headline: doc.title,
        description: doc.summary || "",
        image: doc.cover ? [doc.cover] : undefined,
        author: { "@type": "Organization", name: "CoSec Global" },
        publisher: {
          "@type": "Organization",
          name: "CoSec Global",
          logo: {
            "@type": "ImageObject",
            url: "https://www.cosecglobal.com/assets/cosec-logo-blue.png",
          },
        },
        datePublished: doc.publishedAt,
        dateModified: doc.updatedAt || doc.publishedAt,
        mainEntityOfPage: `https://www.cosecglobal.com/kb/${doc.slug || slug}`,
        keywords: Array.isArray(doc.tags) ? doc.tags.join(", ") : undefined,
      }}
    >  <article className="py-12 md:py-16">
        <div className="container-cv max-w-3xl">
          <Link to="/kb" className="inline-flex items-center gap-1 text-sm font-bold text-muted-foreground hover:text-primary">
            <ArrowLeft className="h-4 w-4" /> Knowledge Base
          </Link>
          <h1 className="mt-4 font-display text-3xl md:text-5xl font-bold text-primary tracking-tight leading-[1.05]">{doc.title}</h1>
          {doc.summary && <p className="mt-4 text-lg text-muted-foreground leading-relaxed">{doc.summary}</p>}
          {doc.readMins && (
            <div className="mt-4 inline-flex items-center gap-1 text-xs text-muted-foreground"><Clock className="h-3.5 w-3.5" /> {doc.readMins} min read</div>
          )}

          {Array.isArray(doc.body) && doc.body.length > 0 && (
            <div className="mt-10 space-y-10" data-testid="kb-body">
              {doc.body.map((b, i) => (
                <section key={i}>
                  <h2 className="font-display text-2xl font-bold text-primary tracking-tight">{b.h}</h2>
                  <Bullets items={b.p} skipHref={`/kb/${doc.slug || slug}`} linkify={linkify} />
                </section>
              ))}
            </div>
          )}

          {Array.isArray(doc.faqs) && doc.faqs.length > 0 && (
            <section className="mt-14">
              <h2 className="font-display text-2xl font-bold text-primary tracking-tight">FAQs</h2>
              <div className="mt-5 divide-y divide-border border border-border rounded-2xl bg-white">
                {doc.faqs.map((f, i) => (
                  <details key={i} className="group px-5 py-4">
                    <summary className="cursor-pointer font-display font-bold text-primary">{f.q}</summary>
                    <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{f.a}</p>
                  </details>
                ))}
              </div>
            </section>
          )}
        </div>
      </article>

      <RelatedArticles
        items={related}
        current={{ slug: doc.slug || slug, tags: doc.tags, category: doc.category }}
        basePath="/kb"
        title="Related help articles"
        testId="kb-related"
      />
    </SiteLayout>
  );
}
